import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CommonRootState } from '@common/types/common-root-state-type.d';
import { WebRootState } from '@website/store/web-store';
import { 
  selectNodeEventStatsData,
  selectNodeEventStatsState
} from '../BotUsageDashboardSelectors';
import { Loader2 } from 'lucide-react';
import { EventType } from '@common/models/node-event';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// Define colors for event types (add more as needed)
const EVENT_TYPE_COLORS: Record<EventType, string> = {
  [EventType.NODE_ENTRY]: '#8884d8',
  [EventType.NODE_EXIT]: '#82ca9d',
  [EventType.PROCESS]: '#ffc658',
  [EventType.DECISION]: '#ff8042',
  [EventType.ERROR]: '#ff4d4f',
  [EventType.INCOMING_MESSAGE]: '#0088FE',
  [EventType.OUTGOING_MESSAGE]: '#00C49F',
  [EventType.SESSION_STARTED]: '#FFBB28',
  [EventType.SESSION_ENDED]: '#FF8042',
  [EventType.CREDIT_USAGE]: '#a4de6c',
  [EventType.TOOL_EVENT]: '#DEB887'
};

interface TableRowData {
  name: string;
  value: number;
}

/**
 * NodeEventDistributionTab Component
 * 
 * Displays the distribution of different node event types and process types in tables.
 * Rows are clickable and navigate to a filtered session list.
 */
export const NodeEventDistributionTab: React.FC = () => {
  const navigate = useNavigate();
  // Get data from Redux store
  const nodeEventStatsState = useSelector((state: WebRootState) => 
    selectNodeEventStatsState(state)
  );
  const nodeEventStatsData = useSelector((state: WebRootState) => 
    selectNodeEventStatsData(state)
  );

  // Extract loading state
  const isLoading = nodeEventStatsState.loading;

  // --- Debugging Logs --- 
  useEffect(() => {
    if (!isLoading && nodeEventStatsData) {
      console.log('[NodeEventDistributionTab] nodeEventStatsData:', nodeEventStatsData);
      console.log('[NodeEventDistributionTab] nodeEventStatsData.processTypeDistribution:', nodeEventStatsData.processTypeDistribution);
    }
  }, [isLoading, nodeEventStatsData]);
  // --- End Debugging Logs ---

  // Transform and sort eventsByType
  const eventTypeTableData: TableRowData[] = nodeEventStatsData?.eventsByType 
    ? Object.entries(nodeEventStatsData.eventsByType)
        .map(([name, value]): TableRowData => ({ name: name as EventType, value: Number(value) }))
        .filter(item => item.value > 0)
        .sort((a, b) => b.value - a.value)
    : [];

  // Transform and sort processTypeDistribution
  const processTypeTableData: TableRowData[] = nodeEventStatsData?.processTypeDistribution
    ? Object.entries(nodeEventStatsData.processTypeDistribution)
        .map(([name, value]): TableRowData => ({ name: name, value: Number(value) }))
        .filter(item => item.value > 0)
        .sort((a, b) => b.value - a.value)
    : [];

  // --- Debugging Log for Process Table Data ---
  useEffect(() => {
      console.log('[NodeEventDistributionTab] processTypeTableData:', processTypeTableData);
  }, [processTypeTableData]);
  // --- End Debugging Log ---

  // --- Click Handlers ---
  const handleEventTypeClick = (eventType: string) => {
    navigate(`/sessions?eventType=${encodeURIComponent(eventType)}`);
  };

  const handleProcessTypeClick = (processType: string) => {
    navigate(`/sessions?processType=${encodeURIComponent(processType)}`);
  };
  // --- End Click Handlers ---

  return (
    <div className="space-y-6"> {/* Wrap cards in a div for spacing */}
      {/* Card 1: Main Event Type Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Node Event Distribution</CardTitle>
          <CardDescription>Breakdown of recorded node event types, sorted by count</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading || !nodeEventStatsData ? (
            <div className="h-60 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : eventTypeTableData.length === 0 ? (
             <div className="h-60 flex items-center justify-center text-slate-500">
               No node event data available.
              </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[70%]">Event Type</TableHead>
                  <TableHead className="text-right">Count</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {eventTypeTableData.map((event) => (
                  <TableRow 
                    key={event.name}
                    onClick={() => handleEventTypeClick(event.name)}
                    className="cursor-pointer hover:bg-muted/50"
                  >
                    <TableCell className="font-medium">{event.name}</TableCell>
                    <TableCell className="text-right">{event.value.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Card 2: Process Type Distribution */}
      {/* Only show this card if there is process data */}
      {!isLoading && processTypeTableData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Process Event Breakdown</CardTitle>
            <CardDescription>Distribution of specific types within PROCESS events</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[70%]">Process Type</TableHead>
                  <TableHead className="text-right">Count</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {processTypeTableData.map((process) => (
                  <TableRow 
                    key={process.name}
                    onClick={() => handleProcessTypeClick(process.name)}
                    className="cursor-pointer hover:bg-muted/50"
                  >
                    <TableCell className="font-medium">{process.name}</TableCell>
                    <TableCell className="text-right">{process.value.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}; 